import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ToastQueue } from '@adobe/react-spectrum';
import { useViewState } from 'app/context/ViewStateContext';
import type { SavedView } from 'app/services/savedViewsApi';
import {
  generateViewStateDeepLink,
  ViewStateParams
} from 'utils/viewStateDeepLink';
import { useSavedViewsSlice } from '../slice';
import {
  selectSavedViewsSaving,
  selectSavedViewsError
} from '../slice/selectors';
import { MY_STUFF_VIEW_NAME } from '../constants';
import styles from './SaveViewModal.module.scss';

const NAME_MAX_LENGTH = 60;
const DESCRIPTION_MAX_LENGTH = 500;

interface SaveViewModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  savedView?: SavedView | null;
  getCurrentViewState?: () => ViewStateParams;
}

export const SaveViewModal = memo(
  ({
    isOpen,
    onDismiss,
    savedView = null,
    getCurrentViewState: getCurrentViewStateProp
  }: SaveViewModalProps) => {
    const { actions } = useSavedViewsSlice();
    const dispatch = useDispatch();
    const location = useLocation();
    const getCurrentViewStateFromContext = useViewState();
    const saving = useSelector(selectSavedViewsSaving);
    const sliceError = useSelector(selectSavedViewsError);
    const isEditMode = savedView !== null;

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [nameTouched, setNameTouched] = useState(false);
    const pendingSaveRef = useRef(false);
    const pendingActionRef = useRef<'create' | 'edit' | null>(null);
    const prevSavingRef = useRef(false);
    const nameInputRef = useRef<HTMLInputElement>(null);

    const resolveCurrentViewState = useCallback((): ViewStateParams | null => {
      const getter = getCurrentViewStateProp ?? getCurrentViewStateFromContext;
      return getter ? getter() : null;
    }, [getCurrentViewStateProp, getCurrentViewStateFromContext]);

    const buildCustomViewLink = useCallback(() => {
      const viewState = resolveCurrentViewState();
      if (!viewState) {
        return savedView?.customViewLink ?? '';
      }
      return generateViewStateDeepLink(viewState, location.pathname);
    }, [location.pathname, resolveCurrentViewState, savedView?.customViewLink]);

    const resetForm = useCallback(() => {
      setName('');
      setDescription('');
      setNameTouched(false);
      pendingSaveRef.current = false;
      pendingActionRef.current = null;
    }, []);

    const handleDismiss = useCallback(() => {
      resetForm();
      onDismiss();
    }, [onDismiss, resetForm]);

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      if (savedView) {
        setName(savedView.name);
        setDescription(savedView.description ?? '');
      } else {
        setName('');
        setDescription('');
      }
      setNameTouched(false);
      pendingSaveRef.current = false;
      pendingActionRef.current = null;
      nameInputRef.current?.focus();
    }, [isOpen, savedView]);

    const trimmedName = name.trim();
    const isNameMissing = trimmedName.length === 0;
    const isNameTooLong = name.length > NAME_MAX_LENGTH;
    const isNameReserved =
      trimmedName.toLowerCase() === MY_STUFF_VIEW_NAME.toLowerCase();
    const isDescriptionTooLong = description.length > DESCRIPTION_MAX_LENGTH;
    const showNameError =
      nameTouched &&
      (isNameMissing || isNameTooLong || isNameReserved) &&
      !saving;
    const isSaveDisabled =
      saving ||
      isNameMissing ||
      isNameTooLong ||
      isNameReserved ||
      isDescriptionTooLong;

    const handleSave = () => {
      setNameTouched(true);

      if (
        isNameMissing ||
        isNameTooLong ||
        isNameReserved ||
        isDescriptionTooLong
      ) {
        return;
      }

      if (isEditMode && savedView) {
        // Editing only ever changes name/description - never touch the
        // view's saved configuration, which is unrelated to whatever
        // filters happen to be live on screen right now.
        pendingSaveRef.current = true;
        pendingActionRef.current = 'edit';
        dispatch(
          actions.updateView({
            id: savedView.id,
            name: trimmedName,
            description: description.trim() || undefined,
            originalName: savedView.name,
            originalDescription: savedView.description
          })
        );
        return;
      }

      const customViewLink = buildCustomViewLink();
      if (!customViewLink) {
        ToastQueue.negative(
          'Unable to capture the current view. Try again after filters load.',
          { timeout: 5000 }
        );
        return;
      }

      pendingSaveRef.current = true;
      pendingActionRef.current = 'create';
      dispatch(
        actions.saveView({
          name: trimmedName,
          description: description.trim() || undefined,
          customViewLink
        })
      );
    };

    useEffect(() => {
      const wasSaving = prevSavingRef.current;
      prevSavingRef.current = saving;

      if (!wasSaving || saving || !pendingSaveRef.current) {
        return;
      }

      pendingSaveRef.current = false;
      const action = pendingActionRef.current;
      pendingActionRef.current = null;

      if (sliceError) {
        ToastQueue.negative(sliceError, { timeout: 5000 });
        return;
      }

      ToastQueue.positive(
        action === 'edit'
          ? 'View updated successfully.'
          : 'View saved successfully.',
        { timeout: 5000 }
      );
      handleDismiss();
    }, [handleDismiss, saving, sliceError]);

    if (!isOpen) {
      return null;
    }

    return ReactDOM.createPortal(
      <div
        className={styles.modalOverlay}
        role="presentation"
        data-testid="save-view-modal-overlay"
      >
        <div
          className={styles.popupContainer}
          role="dialog"
          aria-modal="true"
          aria-labelledby="save-view-title"
          data-testid="save-view-modal"
        >
          <h2 id="save-view-title" className={styles.title}>
            {isEditMode ? 'Edit View' : 'Save View'}
          </h2>

          <div className={styles.formSection}>
            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="save-view-name">
                View name <span className={styles.requiredMark}>*</span>
              </label>
              <input
                ref={nameInputRef}
                id="save-view-name"
                className={`${styles.input} ${
                  showNameError ? styles.inputError : ''
                }`}
                value={name}
                maxLength={NAME_MAX_LENGTH}
                autoComplete="off"
                onChange={event => setName(event.target.value)}
                onBlur={() => setNameTouched(true)}
                placeholder="Enter view name"
                disabled={saving}
                data-testid="save-view-name-input"
              />
              {showNameError && isNameMissing && (
                <p className={styles.errorMessage}>View name is required.</p>
              )}
              {showNameError && !isNameMissing && isNameTooLong && (
                <p className={styles.errorMessage}>
                  View name must be {NAME_MAX_LENGTH} characters or fewer.
                </p>
              )}
              {showNameError &&
                !isNameMissing &&
                !isNameTooLong &&
                isNameReserved && (
                  <p className={styles.errorMessage}>
                    "{MY_STUFF_VIEW_NAME}" is a reserved name. Please choose a
                    different name.
                  </p>
                )}
              <span className={styles.charCount}>
                {name.length}/{NAME_MAX_LENGTH}
              </span>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="save-view-description">
                View description
              </label>
              <textarea
                id="save-view-description"
                className={`${styles.textarea} ${
                  isDescriptionTooLong ? styles.inputError : ''
                }`}
                value={description}
                maxLength={DESCRIPTION_MAX_LENGTH}
                onChange={event => setDescription(event.target.value)}
                placeholder="Enter view description (optional)"
                disabled={saving}
                data-testid="save-view-description-input"
              />
              {isDescriptionTooLong && (
                <p className={styles.errorMessage}>
                  Description must be {DESCRIPTION_MAX_LENGTH} characters or
                  fewer.
                </p>
              )}
              <span className={styles.charCount}>
                {description.length}/{DESCRIPTION_MAX_LENGTH}
              </span>
            </div>
          </div>

          <div className={styles.buttonRow}>
            <button
              type="button"
              className={styles.buttonSecondary}
              onClick={handleDismiss}
              disabled={saving}
              data-testid="save-view-cancel-button"
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.buttonPrimary}
              onClick={handleSave}
              disabled={isSaveDisabled}
              data-testid="save-view-save-button"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }
);

SaveViewModal.displayName = 'SaveViewModal';
